import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2, Search, Coins, School as SchoolIcon, Package } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getAdministrations } from '../api';

const numFmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const moneyFmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));

/** Landing page listing every education administration with quick metrics. */
export default function AdministrationsListPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => { getAdministrations().then(setData).finally(() => setLoading(false)); }, []);

  const rows = useMemo(() => {
    const list = data?.administrations || [];
    const query = q.trim().toLowerCase();
    if (!query) return list;
    return list.filter((r) => (r.name || '').toLowerCase().includes(query));
  }, [data, q]);

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="administrations-page">
        <div className="mb-6">
          <div className="text-[11px] font-semibold tracking-widest" style={{ color: 'var(--se-violet-2)' }}>EDUCATION · ADMINISTRATIONS</div>
          <h1 className="se-title mt-1">الإدارات التعليمية</h1>
          <div className="se-subtitle">استعرض تفاصيل كل إدارة تعليمية والتوريدات المخصصة لها</div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative">
            <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="se-input w-72 pr-8" placeholder="بحث عن إدارة..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" />
          </div>
          <div className="text-slate-500 text-[11.5px] mr-auto">{numFmt(rows.length)} إدارة</div>
        </div>

        {loading ? (
          <div className="se-panel p-12 text-center"><Loader2 size={26} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>
        ) : rows.length === 0 ? (
          <div className="se-panel p-12 text-center text-slate-400">لا توجد إدارات مطابقة</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-grid">
            {rows.map((a) => (
              <button
                key={a.name}
                onClick={() => nav(`/administrations/${encodeURIComponent(a.name)}`)}
                data-testid={`admin-card-${a.name}`}
                className="se-panel p-4 text-right hover:-translate-y-0.5 transition-transform cursor-pointer">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(109,93,251,0.2)' }}>
                    <Building2 size={16} style={{ color: '#9D8CFF' }} />
                  </div>
                  <div className="text-[14.5px] font-bold" style={{ color: '#F1EEFB' }}>{a.name}</div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <Stat label="مدارس" value={numFmt(a.schools)} tint="#38D6C7" />
                  <Stat label="قطعة" value={numFmt(a.allocated_qty)} tint="#9D8CFF" />
                  <Stat label="ر.س" value={moneyFmt(a.allocated_value)} tint="#F59E0B" />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, tint }) {
  return (
    <div>
      <div className="text-[10px] text-slate-500 uppercase tracking-widest">{label}</div>
      <div className="text-[15px] font-bold tabular-nums mt-0.5" style={{ color: tint }}>{value}</div>
    </div>
  );
}
